import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import EmailAddress from "_shared/email-addresses/email-address";
import EmailAddressSchema from "_shared/email-addresses/email-address-schema";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import NameSchema from "_shared/names/name-schema";
import { z } from "zod";

export type UserCoworkerJSON = z.infer<typeof UserCoworkerSchema.UserCoworkerJSONZ>;
export type UserCoworkerParams = z.infer<typeof UserCoworkerSchema.UserCoworkerZ>;

export default class UserCoworkerSchema {
  public static readonly UserCoworkerJSONZ = z.object({
    id: z.uuid().nullish(),
    legalEntityId: z.uuid(),
    entityName: z.string(),
    userId: z.uuid(),
    email: z.email(),
    firstName: z.string(),
    lastName: z.string(),
    individualId: z.uuid(),
    avatarId: z.uuid().nullish(),
  });

  public static readonly UserCoworkerZ = z.object({
    id: z.instanceof(Guid).optional(),
    legalEntityId: z.instanceof(Guid),
    entityName: z.string(),
    userId: z.instanceof(Guid),
    email: z.instanceof(EmailAddress),
    name: z.instanceof(Name),
    individualId: z.instanceof(Guid),
    avatarId: z.instanceof(Guid).optional(),
  });

  public static validate(values: unknown): UserCoworkerParams {
    return parseOrThrow(UserCoworkerSchema.UserCoworkerZ, values, {
      logError: true,
      schemaName: "UserCoworkerSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): UserCoworkerJSON {
    return parseOrThrow(UserCoworkerSchema.UserCoworkerJSONZ, json, {
      logError: true,
      schemaName: "UserCoworkerSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is UserCoworkerParams {
    try {
      UserCoworkerSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is UserCoworkerJSON {
    try {
      UserCoworkerSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: UserCoworkerJSON): UserCoworkerParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(UserCoworkerSchema.UserCoworkerJSONZ, json, {
      logError: true,
      schemaName: "UserCoworkerSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return UserCoworkerSchema.codec.decode(parsed);
  }

  public static serialize(UserCoworker: UserCoworkerParams): UserCoworkerJSON {
    if (isNullOrUndefined(UserCoworker)) return UserCoworker;
    const parsed = UserCoworkerSchema.validate(UserCoworker);
    if (isNullOrUndefined(parsed)) return parsed;
    return UserCoworkerSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(UserCoworkerSchema.UserCoworkerJSONZ, UserCoworkerSchema.UserCoworkerZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id) ?? undefined,
      legalEntityId: Guid.fromJSON(data.legalEntityId)!,
      entityName: data.entityName,
      userId: Guid.fromJSON(data.userId)!,
      email: new EmailAddress(EmailAddressSchema.deserialize(data.email)),
      name: new Name(NameSchema.deserialize({ firstName: data.firstName, lastName: data.lastName })),
      individualId: Guid.fromJSON(data.individualId)!,
      avatarId: Guid.fromJSON(data.avatarId) ?? undefined,
    }),
    encode: (coworker) => {
      if (!coworker.name.firstName || !coworker.name.lastName) {
        throw new Error("Both firstName and lastName are required for serialization");
      }
      return {
        id: coworker.id?.toJSON(),
        legalEntityId: coworker.legalEntityId.toJSON(),
        entityName: coworker.entityName,
        userId: coworker.userId.toJSON(),
        email: coworker.email.toJSON(),
        firstName: coworker.name.firstName,
        lastName: coworker.name.lastName,
        individualId: coworker.individualId.toJSON(),
        avatarId: coworker.avatarId ? coworker.avatarId.toJSON() : undefined,
      };
    },
  });
}
